// src/oura/requests/files.ts
//
// What a client sent besides text: screenshots, photos, PDFs and text files.
// A screenshot is stored once it is uploaded (`/change-requests/uploads`) and
// its `fileKey` travels with the request. PDFs and text files are not stored:
// their words are read in the browser and kept in the request's text.

export type FileKind = "image" | "pdf" | "text" | "other";

export interface Attachment {
    id: string;
    name: string;
    kind: FileKind;
    url: string;
    size: number | null;
    /** The file itself while it is being read; absent on stored ones. */
    file?: File;
    /** The stored key, once a screenshot has been uploaded (`original.fileKeys`). */
    fileKey?: string;
}

/** What staff may add: images, PDFs and plain text or email files. */
export const ACCEPT = "image/*,application/pdf,.txt,.eml,text/plain";

export function kindOf(type: string, name: string): FileKind {
    if (type.startsWith("image/")) return "image";
    if (type === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
    if (type.startsWith("text/") || /\.(txt|eml)$/i.test(name)) return "text";
    return "other";
}

/** Limits on what staff can add, so a big drop cannot freeze the tab. */
export const MAX_FILES = 10;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

/** Why these files cannot be added to the ones already there, or null when they can. */
export function filesError(have: Attachment[], incoming: File[]): string | null {
    for (const f of incoming) {
        const name = f.name || "That file";
        if (kindOf(f.type, name) === "other") return `${name} is not a screenshot, photo, PDF or text file.`;
        if (f.size > MAX_FILE_BYTES) return `${name} is larger than 10 MB.`;
    }
    if (have.length + incoming.length > MAX_FILES) return `Add no more than ${MAX_FILES} files.`;
    const total = have.reduce((n, f) => n + (f.size ?? 0), 0) + incoming.reduce((n, f) => n + f.size, 0);
    if (total > MAX_TOTAL_BYTES) return "The files must add up to no more than 25 MB.";
    return null;
}

let seq = 0;
export function toAttachment(file: File): Attachment {
    const name = file.name || (file.type.startsWith("image/") ? "Pasted screenshot.png" : "File");
    return { id: `f${Date.now()}-${seq++}`, name, kind: kindOf(file.type, name), url: URL.createObjectURL(file), size: file.size, file };
}

export function sizeText(bytes: number | null): string {
    if (bytes === null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The text inside PDFs and text files, for the reader, plus the names of any
 * that could not be read. Images need the AI, so they add nothing here.
 */
export async function textOf(files: Attachment[]): Promise<{ text: string; unreadable: string[] }> {
    const parts: string[] = [];
    const unreadable: string[] = [];
    for (const f of files) {
        if (!f.file) continue;
        try {
            if (f.kind === "text") parts.push(await f.file.text());
            if (f.kind === "pdf") {
                const { pdfText } = await import("../lib/quotePdf");
                parts.push(joinWrapped(await pdfText(f.file)));
            }
        } catch {
            unreadable.push(f.name);
        }
    }
    return { text: parts.filter((p) => p.trim()).join("\n\n"), unreadable };
}

/**
 * A PDF breaks lines where the page wraps. Join those back into sentences, but
 * keep the break after a heading (a short line with no full stop) and between
 * paragraphs.
 */
function joinWrapped(text: string): string {
    const lines = text.split("\n").map((l) => l.trim());
    let out = "";
    lines.forEach((line, i) => {
        if (i === 0) {
            out = line;
            return;
        }
        const prev = lines[i - 1];
        const heading = prev.length > 0 && prev.length < 60 && !/[.!?,;:]$/.test(prev);
        out += !line || !prev || heading ? `\n${line}` : ` ${line}`;
    });
    return out;
}
